import { computed, ref } from "vue";

// Per-device toggle for the notifications bell/panel. When off:
//   - the bell's red unread badge is hidden
//   - the panel body swaps to a "Notifications are off" state
//   - "Mark all as read" is hidden (nothing to read)
// The bell itself stays visible so the user can always re-enable
// from the same panel where they turned it off.
//
// Persisted in localStorage — deliberately per-device (matches how
// content-read-state.ts already scopes unread tracking). If cross-
// device sync is needed later, promote to a user-service field.

const storageKey = "tmi-notifications-pref-v1";

function canUseLocalStorage(): boolean {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readStoredEnabled(): boolean {
    if ( !canUseLocalStorage() ) return true;
    try {
        const raw = window.localStorage.getItem( storageKey );
        // Absent key = default ON. Any explicit value other than "off"
        // is also treated as ON so a corrupt/legacy value fails safe.
        return raw !== "off";
    } catch {
        return true;
    }
}

function persist( value: boolean ): void {
    if ( !canUseLocalStorage() ) return;
    try {
        window.localStorage.setItem( storageKey, value ? "on" : "off" );
    } catch {
        // Ignore storage failures; the preference is a UI enhancement.
    }
}

// Module-level ref so every caller shares the same reactive state —
// matches the pattern in content-read-state.ts.
const enabled = ref< boolean >( readStoredEnabled() );

export function useNotificationsPreference() {
    const notificationsEnabled = computed< boolean >( () => enabled.value );

    function setEnabled( value: boolean ): void {
        enabled.value = value;
        persist( value );
    }

    function toggle(): void {
        setEnabled( !enabled.value );
    }

    return { notificationsEnabled, setEnabled, toggle };
}
